import { get, startCase, toLower } from "lodash";

export const ASSET_TYPE = {
  TITLE: "titleName",
  ID: "idType",
};

export const CONTENT_FINDER = {
  recordTypeFormLabel: "Select Record Type",
  dashboardHeader: "Legacy GMSC",
  idTypeFormLabel: "Select ID Type",
  episodeDetailsPage: "Episodes",
  versionsPage: "Versions",
  mastersPage: "Masters",
};

export const NO_EPISODES_ERROR = {
  title: "No Episodes Found",
  message: "We couldn't find any episodes for this title.",
};

export const NO_VERSIONS_ERROR = {
  title: "No Records Found",
  message: "We couldn't find any records for this title.",
};

export const DISPLAY_FALLBACK = "-";

export const VIDEO_SECTION = {
  title: "Video",
  emptyMessage: "No Tracks Found",
  headers: [
    "Type",
    "Sub-Type",
    "Frame Rate",
    "Aspect Ratio",
    "Resolution",
    "Scan Type",
    "Burned In Subtitles",
    "Duration",
    "Date Added",
  ],
};

export const AUDIO_SECTION = {
  title: "Audio",
  emptyMessage: "No Tracks Found",
  headers: [
    "Audio Content Kind",
    "Sound Field Group Layout",
    "Sound Field Group Type",
    "Channel",
    "Language",
  ],
};

export const LEGACY_AUDIO_SECTION = {
  title: "Audio",
  emptyMessage: "No Tracks Found",
  headers: ["Sub-Type", "Language", "Track Version", "Date Added"],
};

export const TIMED_TEXT_SECTION = {
  title: "Timed Text",
  emptyMessage: "No Tracks Found",
  headers: ["Language", "Description", "Date Added"],
};

export const NOTE_SECTION = {
  title: "Notes",
  emptyMessage: "No Notes Found",
};

export const FEATURE_VERSION_COLUMNS = [
  { id: "cid", label: "CID", width: "13%" },
  { id: "network", label: "Network", width: "14%" },
  { id: "aspectRatio", label: "Aspect Ratio", width: "13%" },
  { id: "scanType", label: "Scan Type", width: "13%" },
  { id: "audio", label: "Audio", width: "12%" },
  { id: "language", label: "Language", width: "13%" },
  { id: "breaks", label: "Breaks", width: "10%" },
  { id: "status", label: "Status", width: "12%" },
];

export const ALTERNATE_TITLES = {
  title: "Alternate Titles",
  error: "No alternate titles available.",
}

export const SERIES_TYPES = [
  'series',
  'sports series',
  'news series'
];

export const EPISODE_TYPES = [
  'episode',
  'element',
  'sporting event',
  'news episode',
];

export const FEATURE_TYPES = [
  'feature film',
  'clip',
  'sports clip',
  'trailer',
  'special',
  'short',
  'news program',
];

export const TITLE_TYPE_GROUPS = {
  SERIES: 'SERIES',
  EPISODE: 'EPISODE',
  FEATURE: 'FEATURE',
};

export type TitleTypeGroup =
  typeof TITLE_TYPE_GROUPS[keyof typeof TITLE_TYPE_GROUPS];

export const getTitleTypeGroup = (titleData) => {
  const titleType = toLower(get(titleData, 'TitleType.Name', ''));
  if (SERIES_TYPES.includes(titleType)) return TITLE_TYPE_GROUPS.SERIES;
  if (EPISODE_TYPES.includes(titleType)) return TITLE_TYPE_GROUPS.EPISODE;
  if (FEATURE_TYPES.includes(titleType)) return TITLE_TYPE_GROUPS.FEATURE;
  return null;
};

export const TITLE_BAR_COLUMNS = [
  {
    id: 'TitleId',
    label: 'TITLE ID',
    path: 'TitleId',
    show: [
      TITLE_TYPE_GROUPS.SERIES,
      TITLE_TYPE_GROUPS.EPISODE,
      TITLE_TYPE_GROUPS.FEATURE,
    ],
  },
  {
    id: 'TitleType',
    label: 'TITLE TYPE',
    path: 'TitleType.Name',
    show: [
      TITLE_TYPE_GROUPS.SERIES,
      TITLE_TYPE_GROUPS.EPISODE,
      TITLE_TYPE_GROUPS.FEATURE,
    ],
  },
  {
    id: 'ReleaseYear',
    label: 'RELEASE YEAR',
    path: 'ReleaseYear',
    show: [
      TITLE_TYPE_GROUPS.SERIES,
      TITLE_TYPE_GROUPS.EPISODE,
      TITLE_TYPE_GROUPS.FEATURE,
    ],
  },
  {
    id: 'OriginalNetwork',
    label: 'ORIG. NETWORK',
    path: 'OriginalNetwork.NetworkCode',
    show: [
      TITLE_TYPE_GROUPS.SERIES,
      TITLE_TYPE_GROUPS.EPISODE,
      TITLE_TYPE_GROUPS.FEATURE,
    ],
  },
  {
    id: 'Language',
    label: 'ORIG. LANGUAGE',
    path: 'Language.Name',
    show: [
      TITLE_TYPE_GROUPS.SERIES,
      TITLE_TYPE_GROUPS.EPISODE,
      TITLE_TYPE_GROUPS.FEATURE,
    ],
    format: (value) => (value ? startCase(toLower(value)) : '-'),
  },
  {
    id: 'Seasons',
    label: 'SEASONS #',
    show: [TITLE_TYPE_GROUPS.SERIES],
    getValue: (data) => get(data, 'Seasons', []).length,
  },
  {
    id: 'Episodes',
    label: 'EPISODES #',
    show: [TITLE_TYPE_GROUPS.SERIES],
    getValue: (data) => get(data, 'SeriesItems', []).length,
  },
  {
    id: 'SeriesName',
    label: 'SERIES NAME',
    path: 'SeriesTitleName',
    show: [TITLE_TYPE_GROUPS.EPISODE],
    isLink: true,
    getLinkPath: (data) =>
      `/legacyGmsc/series/${get(data, 'SeriesTitleId', '')}`,
  },
  {
    id: 'SeasonNumber',
    label: 'SEASON #',
    path: 'SeasonNumber',
    show: [TITLE_TYPE_GROUPS.EPISODE],
  },
  {
    id: 'SeasonEpisodeNumber',
    label: 'EPISODE #',
    path: 'SeasonEpisodeNumber',
    show: [TITLE_TYPE_GROUPS.EPISODE],
  }
];

export const ID_TYPES = [
  {
    label: "CID",
    value: "cid",
  },
  {
    label: "Title ID",
    value: "titleId",
  },
];

export const RECORD_TYPES = [
  {
    label: "ID",
    value: ASSET_TYPE.ID,
  },
  {
    label: "Title Name",
    value: ASSET_TYPE.TITLE,
  },
];

export const MAP_PLACEHOLDER = {
  "idType": {
    "cid": "Search by CID",
    "titleId": "Search by Title ID",
  },
  "titleName": "Search by Title",
};
